import {
  convertToKebab,
  FluidOption,
  FluidOptionButtonType,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { FluidFormElementTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-form-element-translation-options.interface';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Observable } from 'rxjs';

import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import { ButtonGroupOption } from './components/fluid-button-group.components';

/**
 * @displayName Button Group
 *
 * @contextData FLUID Button Group is used to display contextually related, but mutually exclusive options as discrete buttons in a single row. Similar to Radio Group, only 1 option can be selected at a time, and there is no way to deselect all options once one has been selected. By default, Button Group will automatically select the first non-disabled option in the group unless a selected option is provided.
 *
 * Button Group is primarily used to toggle between different "views" within a page, but can also be used in forms as a form field.
 */
@Component({
  tag: 'fluid-button-group',
  styleUrl: 'fluid-button-group.scss',
  shadow: true,
})
export class FluidButtonGroup
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * If true, all @Watch methods are temporarily disabled to allow
   * Props/States to be updated without triggering unnecessary re-renders.
   */
  private _disableWatch = false;

  /**
   * String used for "name" attribute of underlying radio buttons.
   * (NOTE: "@ts-ignore" is required because the reference is only read
   * externally in Button Group's functional components).
   */
  //@ts-ignore
  private _name: string;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidButtonGroupElement;

  // ================================================================ //
  // -- States

  /**
   * Derived state of "options" array. This allows us to manipulate
   * individual options' properties (e.g. selected status) without
   * mutating the original objects.
   */
  @State() _options: FluidOption[];

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Name used to associate the individual options together as a group.
   * If Button Group is used as a form element, set to "controlName"
   * of parent form field.
   */
  @Prop({ mutable: true }) name: string;

  /**
   * Array of FluidOption objects to populate the Button Group.
   * NOTE: Use this Prop INSTEAD OF deprecated "labels" Prop.
   */
  @Prop({ mutable: true }) options: FluidOption[] = [];

  @Watch('options')
  onOptionsChange(newOpts: FluidOption[], oldOpts: FluidOption[]) {
    if (this._disableWatch || Utils.isEqual(newOpts, oldOpts)) return;
    this.iconDeprecationWarning();
    this.updateOptions();
  }

  /**
   * The current selected option's "value" property. Dynamically updated
   * when the selected option changes. If provided, sets the option with
   * matching "value" property as selected. If provided value does NOT
   * match any existing FluidOption objects in the "options" array OR is
   * "undefined", REMOVES the selected option and DESELECTS all options in
   * the group. NOTE: the selected option can also be set via an
   * individual option's "selected" property.
   */
  @Prop({ mutable: true }) selectedValue: string;

  @Watch('selectedValue')
  onSelectedValueChange(newValue: string, oldValue: string) {
    if (this._disableWatch || newValue === oldValue) return;
    this.updateState(this.getOptionByValue(this.selectedValue));
  }

  /**
   * If true, all options will be unselected by default. If false,
   * Button Group will automatically select the first non-disabled option
   * in the "options" array IF no other selected option is defined (i.e.
   * the "selectedLabel" Prop, the "selectedValue" Prop, or an option
   * with "selected: true" will override this Prop's functionality).
   */
  @Prop({ mutable: true }) preventDefault = false;

  /**
   * Observable used to debounce selected option change. If provided,
   * changes to the selected option are deferred until the Observable is
   * executed (i.e. when an option is clicked, the Button Group will emit
   * the option's value, but won't visually update the selected option
   * until the Observable executes).
   */
  @Prop({ mutable: true }) debounceChange: Observable<boolean>;

  /**
   * Disables the entire Button Group if true. NOTE: individual option(s)
   * can be disabled via the FluidOption "disabled" property.
   */
  @Prop({ mutable: true }) disabled = false;

  /**
   * Sets the aesthetic style of Button Group's options (see usage examples for details)
   */
  @Prop({ mutable: true }) optionButtonType: FluidOptionButtonType =
    FluidOptionButtonType.DEFAULT;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the button group in order
   * to help facilitate
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Events

  /**
   * Emits the FluidOption "value" property of an option when it's selected.
   */
  @Event({ bubbles: false }) selected: EventEmitter<FluidOption> &
    EventEmitter<string>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - Sets the global theme
   * @action: setName - Converts the "name" Prop to kebab-case OR generates unique ID
   * @action: mapLabelsToOptions - if "labels" Prop used, maps values to "options" Prop
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    this.setName();
    this.mapLabelsToOptions();
    useI18n.bind(this)({
      init: () => (this._i18nBound = true),
    });
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: initializeState - creates internal state from "options" Prop
   */
  componentWillLoad(): Promise<void> | void {
    this.iconDeprecationWarning();
    this.selectedLabelDeprecationWarning();
    this.setOptions();
    this.initializeState();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action: re-enable @Watch methods (if disabled)
   */
  componentDidLoad() {
    this.initializeState();
    this._disableWatch = false;
  }

  /**
   * Called just after the component updates. It's never called during the first render().
   * @action: re-enable @Watch methods (if disabled)
   */
  componentDidUpdate() {
    this._disableWatch = false;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * If "options" Prop is NOT provided, maps deprecated "labels" Prop
   * values to FluidOption objects in "options" Prop. Each new FluidOption
   * object has the same value for both "label" & "value" properties.
   * @param isUpdate - whether to bypass check for defined "options" Prop
   */
  mapLabelsToOptions(isUpdate = false): void {
    if (!isUpdate && (!this.labels?.length || !!this.options?.length)) return;

    Utils.deprecationWarning(
      this.host,
      'Prop',
      'labels',
      'options',
      'FluidOption'
    );

    this._disableWatch = true;
    this.options = this.labels.map((label) => ({ label, value: label }));
  }

  iconDeprecationWarning(): void {
    if (this.options.some((option) => !!option.icon)) {
      Utils.deprecationWarning(
        this.host,
        'options Prop',
        'FluidOption.icon',
        'FluidOption.iconBefore or FluidOption.iconAfter',
        'FluidIconConfig'
      );
    }
  }

  /**
   * If deprecated "selectedLabel" Prop is used, outputs a console warning.
   */
  selectedLabelDeprecationWarning(): void {
    if (!this.selectedLabel) return;

    Utils.deprecationWarning(
      this.host,
      'Prop',
      'selectedLabel',
      'selectedValue',
      'string'
    );
  }

  /**
   * Creates "name" attribute for underlying radio buttons using "name"
   * Prop value (if provided) or generated unique ID.
   */
  setName(): void {
    this._name = convertToKebab(this.name) ?? generateUniqueId();
  }

  /*
   * Creates internal derived state from "options" array.
   */
  setOptions() {
    this._options = this.options?.map((opt) => ({ ...opt }));
  }

  /**
   * Called when "options" or "labels" Prop updated. Updates internal
   * state with new "options" array & sets the selected option (if
   * possible). If none of the new options are explicitly selected (i.e.
   * no options have property "selected: true"), attempts to retain the
   * previously selected option (if one existed).
   */
  updateOptions() {
    const prevSelectedOpt = this.getSelectedOption();

    this.setOptions();

    const newSelectedOpt = this.getSelectedOption();
    const valueMatch = this.getOptionByValue(prevSelectedOpt?.value);
    const labelMatch = this.getOptionByLabel(prevSelectedOpt?.label);

    switch (true) {
      // If new option with "selected: true" exists, overwrite the previously selected option
      case !!newSelectedOpt:
        this.updateState(newSelectedOpt);
        break;
      // If no new options are explicitly selected, try to retain old selected option by its value
      case !!valueMatch:
        this.updateState(valueMatch);
        break;
      // If no new options match old "selectedValue", try to retain old selected option by its label
      case !!labelMatch:
        this.updateState(labelMatch);
        break;
      // If none of the previous conditions are true, no selected option exists
      default:
        this.updateState();
    }
  }

  /**
   * Sets the initial selected option. If "selectedLabel" & "selectedValue"
   * are undefined AND no options have "selected: true", sets selected option
   * to the 1st non-disabled option UNLESS "preventDefault" is true.
   */
  initializeState() {
    let selectedOpt: FluidOption;
    switch (true) {
      // Check "selectedLabel" first since it originally had precedence
      case !isNullOrUndefined(this.selectedLabel):
        selectedOpt = this.getOptionByLabel(this.selectedLabel);
        break;
      // If "selectedLabel" is undefined, check "selectedValue" next
      case !isNullOrUndefined(this.selectedValue):
        selectedOpt = this.getOptionByValue(this.selectedValue);
        break;
      // Check all options for "selected: true" OR find first non-disabled option
      default:
        selectedOpt = this.getSelectedOption() ?? this.getFirstEnabledOption();
    }

    this.updateState(selectedOpt);
  }

  /**
   * Updates internal state, "selectedLabel", & "selectedValue" to reflect
   * the currently selected option. Called any time the selected option
   * changes, or when a Prop with an attached @Watch method is updated.
   * @param selectedOpt - the new selected option
   */
  updateState(selectedOpt: FluidOption = undefined) {
    this._disableWatch = true;

    this._options.forEach((opt) => (opt.selected = opt === selectedOpt));
    this.selectedLabel = selectedOpt?.label;
    this.selectedValue = selectedOpt?.value;

    this._disableWatch = false;
  }

  /**
   * Returns the 1st FluidOption that matches the provided label.
   * @param label - the target option's label
   * @param options - the array of FluidOptions to search
   */
  getOptionByLabel(
    label: string,
    options: FluidOption[] = this._options
  ): FluidOption {
    return options?.find((opt) => opt.label === label);
  }

  /**
   * Returns the 1st FluidOption that matches the provided value.
   * @param value - the target option's value
   * @param options - the array of FluidOptions to search
   */
  getOptionByValue(
    value: string | boolean,
    options: FluidOption[] = this._options
  ): FluidOption {
    return options?.find((opt) => `${opt.value}` === `${value}`);
  }

  /**
   * Returns the 1st FluidOption with property "selected: true".
   * @param options - the array of FluidOptions to search
   */
  getSelectedOption(options: FluidOption[] = this._options): FluidOption {
    return options?.find((opt) => opt.selected);
  }

  /**
   * Returns the 1st non-disabled option in "options" if "disabled" AND
   * "preventDefault" Props are false. Used to set the component's default
   * selected option on first load if one isn't provided.
   */
  getFirstEnabledOption(): FluidOption {
    if (this.disabled || this.preventDefault) return;
    return this._options?.find((opt) => !opt.disabled);
  }

  /**
   * When an option is clicked, emits the option's value immediately BUT defers
   * change to selected option until "debounceChange" Observable is executed.
   * (i.e. after an option is clicked, the Button Group doesn't visually update
   * the selected option until the Observable executes).
   * @param option - the option that was selected (via click, enter key, etc.)
   * @param event - the click event that is "cancelled" until Observable executes
   */
  debounceClick(option: FluidOption, event: MouseEvent) {
    if (!this.debounceChange || this.disabled || option.disabled) return;

    event.preventDefault();
    this.selected.emit(option.value);

    this.debounceChange.subscribe((canChange: boolean) => {
      if (canChange) {
        this.updateState(option);
      }
    });
  }

  /**
   * When the selected option changes via click, enter key, etc., emits the
   * option's value & triggers an update to the internal state.
   * @param option - the option that was selected (via click, enter key, etc.)
   */
  handleOptionChange(option: FluidOption) {
    this.selected.emit(option.value);
    this.updateState(option);
  }

  // ================================================================ //
  // -- Render
  render() {
    return (
      <Host
        data-test-id={`${this.identifier}-group-btn-wrapper`}
        class={{
          'fluid-button-group--tile-buttons': this.optionButtonType === 'tile',
        }}>
        <div
          role='group'
          data-test-id={`${this.identifier}-btn-group`}
          class={{
            [`fluid-button-group theme-${this.theme}`]: true,
            'tile-buttons':
              this.optionButtonType === FluidOptionButtonType.TILE,
          }}>
          {this._options.map((option: FluidOption) => (
            <ButtonGroupOption
              i18nStore={this._i18nStore}
              fluidTranslate={this.fluidTranslate}
              host={this}
              option={option}
              optionButtonType={this.optionButtonType}
              identifier={this.identifier}
            />
          ))}
        </div>
      </Host>
    );
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "labels" is now deprecated. Instead, use "options" Prop.
   * -- Array of string labels to populate the Button Group. Values from
   * this Prop are now mapped to FluidOption objects in the "options" Prop.
   * @deprecated
   */
  @Prop({ mutable: true }) labels: string[] = [];

  @Watch('labels')
  onLabelsChange(newLabels: string[], oldLabels: string[]) {
    if (this._disableWatch || Utils.isEqual(newLabels, oldLabels)) return;
    this.mapLabelsToOptions(true);
    this.updateOptions();
  }

  /**
   * DEPRECATED: "selectedLabel" is now deprecated. Instead, use
   * "selectedValue" Prop. -- The current selected option's "label"
   * property. Dynamically updated when the selected option changes. If
   * provided, sets the option with matching "label" property as selected.
   * If provided label does NOT match any existing FluidOption objects in
   * the "options" array OR is "undefined", REMOVES the selected option and
   * DESELECTS all options in the group. NOTE: the selected option can
   * also be set via an individual option's "selected" property.
   * @deprecated
   */
  @Prop({ mutable: true }) selectedLabel: string;

  @Watch('selectedLabel')
  onSelectedLabelChange(newLabel: string, oldLabel: string) {
    if (this._disableWatch || newLabel === oldLabel) return;
    this.selectedLabelDeprecationWarning();
    this.updateState(this.getOptionByLabel(this.selectedLabel));
  }

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidFormElementTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound = false;
}
